import { cache } from 'react'
import { QueryClient } from '@tanstack/react-query'

const retryDelay = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 10000)

/**
 * Request-scoped server client: discarded after the RSC response.
 * `staleTime` / `gcTime` do NOT apply “across users” or “across page loads” — only inside this one render pass
 * (e.g. deduping the same `queryKey` if fetched from several components).
 * High `staleTime` avoids redundant refetches within that single tree.
 * `gcTime: Infinity` — a finite value schedules a GC timer that keeps the request's data
 * alive after the response, although it is never read again.
 */
function makeServerQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
        retryDelay
      },
      mutations: {
        gcTime: 0
      }
    }
  })
}

/**
 * Long-lived browser singleton: here `staleTime` / `gcTime` matter for UX and memory across navigations.
 */
function makeBrowserQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        gcTime: 2 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: 1,
        retryDelay
      },
      mutations: {
        gcTime: 0
      }
    }
  })
}

let browserQueryClient: QueryClient | undefined

/**
 * One QueryClient per HTTP request on the server (React `cache`); singleton in the browser.
 * Always use this instead of a module-level singleton on the server — a shared server cache
 * would leak prefetched user data into `dehydrate()` / HTML for other users.
 */
const getServerQueryClient = cache(() => makeServerQueryClient())

export function getQueryClient() {
  if (typeof window === 'undefined') {
    return getServerQueryClient()
  }

  browserQueryClient ??= makeBrowserQueryClient()

  return browserQueryClient
}
